import { fetchApi } from "./services";

export interface TwitchStreamStatus {
  live: boolean;
  configured: boolean;
  login: string;
  title?: string;
  viewerCount?: number;
  startedAt?: string;
  thumbnailUrl?: string;
}

export interface TwitchStreamHealth {
  live: boolean;
  configured: boolean;
  login: string;
}

/** Full Helix stream status (live flag + title/viewers). Also acts as live healthcheck. */
export const fetchTwitchStreamStatus = async (
  channel?: string | null,
): Promise<TwitchStreamStatus | null> => {
  const normalized = channel?.trim();
  const query = normalized ? `?channel=${encodeURIComponent(normalized)}` : "";
  return (await fetchApi<TwitchStreamStatus>(`twitch/stream${query}`)) ?? null;
};

/** Lightweight live healthcheck — use for show/hide gates when metadata is not needed. */
export const fetchTwitchStreamHealth = async (
  channel?: string | null,
): Promise<TwitchStreamHealth | null> => {
  const normalized = channel?.trim();
  const query = normalized ? `?channel=${encodeURIComponent(normalized)}` : "";
  return (await fetchApi<TwitchStreamHealth>(`twitch/stream/health${query}`)) ?? null;
};

export const buildTwitchEmbedSrc = (channel: string, parents: string[]): string => {
  const params = new URLSearchParams({ channel });
  parents.forEach((parent) => {
    if (parent) params.append("parent", parent);
  });
  return `https://player.twitch.tv/?${params.toString()}`;
};

export const buildTwitchChatEmbedSrc = (channel: string, parents: string[]): string => {
  const params = new URLSearchParams({ darkpopout: "" });
  parents.forEach((parent) => {
    if (parent) params.append("parent", parent);
  });
  return `https://www.twitch.tv/embed/${encodeURIComponent(channel)}/chat?${params.toString()}`;
};

export const getTwitchEmbedParents = (): string[] => {
  const parents = new Set<string>();
  if (typeof window !== "undefined" && window.location.hostname) {
    parents.add(window.location.hostname);
  }
  const configured = import.meta.env.VITE_WEB_URL.trim();
  if (configured) {
    parents.add(configured.replace(/^https?:\/\//, "").split("/")[0] ?? configured);
  }
  parents.add("finnhouse.fi");
  parents.add("www.finnhouse.fi");
  parents.add("localhost");
  return [...parents].filter(Boolean);
};

export const buildTwitchChannelUrl = (channel: string): string =>
  `https://www.twitch.tv/${channel}`;

export const TWITCH_PLAYER_IFRAME_SANDBOX = "allow-scripts allow-same-origin";
export const TWITCH_PLAYER_IFRAME_ALLOW = "autoplay; fullscreen";

export const parseTwitchPlayerEmbedChannel = (embedSrc: string): string => {
  try {
    return new URL(embedSrc).searchParams.get("channel")?.toLowerCase() ?? "";
  } catch {
    return "";
  }
};

export const parseTwitchChatEmbedChannel = (chatSrc: string): string => {
  try {
    const segment = new URL(chatSrc).pathname.split("/").filter(Boolean);
    const embedIndex = segment.indexOf("embed");
    if (embedIndex >= 0 && segment[embedIndex + 1]) {
      return decodeURIComponent(segment[embedIndex + 1]).toLowerCase();
    }
  } catch {
    return "";
  }
  return "";
};
